// The observer among the bodies, for the renderer (observer-centric LOD, step 7). One object holds what the frame loop needs of it: the registry (bodies.ts), the flight state (flight.ts) and the
// far shell (farShell.ts), and works out, once a frame, the few numbers the rest of the picture is set by. Nothing here is specific to the home planet except where the registry says so.
//
// Order in a frame:
//   feed     the sun message (smoothed) into the registry                          before the controls' result is taken
//   step     the camera the controls moved -> where the observer is (flight.ts)    returns the camera to write
//   prepare  the pose's numbers: height, near-field fade, the planet's share of the view, shadow     before the light and the exposure
//   (the renderer: exposure from eyeLum, the sun's placement from sunPlacement, the moon's glare from moonGlare, the stars from starView and starSpace)
//   paint    the far shell                                                          after the exposure is known
//
// What the picture does with height, in one place:
//   * the near field (the flat tile: terrain, props, grass, the sea, the cloud volume) is drawn whole below NEAR_FULL_M, its sea grows transparent from there to NEAR_GONE_M (the planet the
//     far shell draws under it shows through), and everything else is hidden from NEAR_HIDE_M up (nothing of the island is more than a pixel by then). The cloud volume is not faded by height:
//     where it gives way to the world painter's cloud (worldCloud.ts) is settled pixel by pixel by the cell's footprint, its range and the patch's edge (handover.ts); height only takes it out
//     over the last stretch of the tile's camera frame (CLOUD_FULL_M to CLOUD_GONE_M), which cannot draw it;
//   * the sky (sky.ts) is one table of what the air sends to the eye along every direction, for wherever the eye is: climbing is only its height changing, so there is no hand-over to
//     another drawing. The stars, the galaxy, the sun and the moon read the same table's transmittance along their own direction (bodyAir.ts, eyeLight.ts), and the night floor of the
//     dome is laid on by the same table's air along each direction (sky.ts);
//   * the exposure is set from the eye's light (eyeLum.ts): the sky's light plus the star's beam weighted by how much of the view is planet, so it goes from the ground's light to that of a
//     surface facing the star as the planet shrinks below, and to the night's in the planet's shadow, with no switch on the air. There is nothing here that is the height's alone.
import * as THREE from 'three';
import { Bodies, nearFade, cloudFade, dot, norm, len } from './bodies';
import { groundShare, eyeLum } from './eyeLum';
import type { EyeLum } from './eyeLum';
import type { BodiesMsg, FarBody, Pose, V3, M3 } from './bodies';
import { Flight } from './flight';
import type { Drive, FlightOut } from './flight';
import { SimClock } from './simclock';
import { FarShell, FAR_MAX_BODIES } from './farShell';
import type { FarShellFrame } from './farShell';
import { WorldCloud } from './worldCloud';
import { worldTiles } from './worldTiles';
import { handover, VOLUME_TOP_M } from './handover';
import { MOON_SIZE_SCALE } from './moon';
import type { MoonGlare } from './moon';

/** Above this height (m) everything of the near field that is not fading out (the sea and the cloud composite) is hidden. */
export const NEAR_HIDE_M = 60e3;
/** The luminance of the star's beam, per unit of its flux, where there is no air to colour it (bodies.ts STAR_RGB). */
const STAR_BEAM_LUM = 0.2126 * 1.0 + 0.7152 * 0.98 + 0.0722 * 0.92;
/** Aerosol's share of the zenith optical depth the stars message states (it states the whole of the air's): the rest is taken as the clean air's. STUB. */
const AEROSOL_TAU = 0.03;
/** Moon (any rock) drawn this many times its true size when it is a speck, easing to true size between these angular radii (rad). */
const SIZE_EASE_LO = 0.3 * Math.PI / 180;
const SIZE_EASE_HI = 3.0 * Math.PI / 180;
/** Where the sun's disc and glare go from the server's direction (valid on the ground) to the registry's (valid anywhere): heights in m. */
const SUN_BLEND_LO = 3e3;
const SUN_BLEND_HI = 30e3;
/** Heights (m) between which the server's own directions to the star and moon (valid at the world's point) give way to the bodies' circles (valid anywhere): the registry's `bias`. */
const BIAS_FROM_M = 10e3;
const BIAS_TO_M = 60e3;

const smooth = (a: number, b: number, x: number): number => { const t = Math.min(1, Math.max(0, (x - a) / (b - a))); return t * t * (3 - 2 * t); };

export interface SpaceSunIn {
  worldPos?: ArrayLike<number>;
  frame?: ArrayLike<number>;
  direction: ArrayLike<number>;
  angularDiameter: number;
  moon?: { direction: ArrayLike<number>; angularDiameter: number } | null;
}

export interface SunPlacement {
  /** Unit direction to the star in the render axes (three's), and the share of its disc the planet hides (0 to 1). */
  dir: V3;
  hidden: number;
  /** The star's angular radius from the eye (rad): the server's near the ground, the true one from where the observer is (the registry's) away from it. */
  angR: number;
  /** True when the registry's view of the star replaces the server's rule for whether it is up (above a few km). */
  away: boolean;
}

export class Space {
  readonly bodies = new Bodies();
  readonly flight: Flight;
  readonly shell = new FarShell();
  /** The planet's cloud from the world painter's tiles (worldCloud.ts): a pass of its own, after the sea. */
  readonly cloud = new WorldCloud();
  /** The simulation's tick now: the observer's time (flight.ts, Drive). */
  readonly clock = new SimClock();
  private bodiesMsg: BodiesMsg | null = null;

  // this frame, set by prepare()
  /** The registry could place the observer this frame. */
  active = false;
  pose: Pose | null = null;
  /** Height above the followed body (m). */
  h = 0;
  /** Share of the near field drawn (1 on the ground, 0 from NEAR_GONE_M up, or at another body). */
  nf = 1;
  /** Share of the cloud volume the tile's camera frame can draw (1 below CLOUD_FULL_M, 0 from CLOUD_GONE_M up, or at another body). The pixel-by-pixel hand-over (handover.ts) is on top of it. */
  nfc = 1;
  /** The cloud volume has nothing left to show: the shell has every pixel the eye can see of it (the eye is higher than the near field's reach plus the volume's top). */
  volGone = false;
  /** The planet's share of the view from the eye, doubled so that it is 1 on the ground (eyeLum.ts). */
  share = 1;
  /** Everything of the near field that is not fading out (the sea and the cloud composite) is hidden. */
  nearOff = false;
  /** The eye is the home planet's observer (the sky pass's table is for it): false at another body. */
  atHome = true;
  inShadow = false;
  private fb: FarBody[] = [];
  private flux = 1;

  constructor(cx: number, cz: number) {
    this.flight = new Flight(this.bodies, cx, cz);
  }

  /** The server's bodies message (bodies.py): masses and circles. The same message again is ignored; null forgets it. */
  setBodies(msg: BodiesMsg | null): void {
    if (msg === this.bodiesMsg) return;
    this.bodiesMsg = msg;
    this.bodies.setEphemeris(msg);
    this.flight.reset();
  }

  /**
   * The sun message (smoothed) into the registry. `flux`: the star's flux at the world; `radiusM`: the planet's radius; `latDeg`: the world's latitude; `ext`: the stars message's extinction;
   * `tick`: the simulation's tick now (NaN if not known), at which the server's bodies are placed.
   */
  feed(sun: SpaceSunIn | null, flux: number, radiusM: number, latDeg: number, ext: ArrayLike<number> | null | undefined, tick = NaN): void {
    if (!sun || !sun.worldPos || !sun.frame || sun.frame.length < 9) return;
    const B = this.bodies;
    B.setRadius(radiusM);
    B.setLatitude(latDeg);
    if (ext && ext.length >= 3) {
      B.setAir([Math.max(0, ext[0] - AEROSOL_TAU), Math.max(0, ext[1] - AEROSOL_TAU), Math.max(0, ext[2] - AEROSOL_TAU)]);
    }
    this.flux = flux > 0 ? flux : 1;
    B.feedSun({
      worldPos: sun.worldPos, frame: sun.frame, direction: sun.direction, angularDiameter: sun.angularDiameter, flux: this.flux,
      moon: sun.moon && sun.moon.angularDiameter > 0 ? { direction: sun.moon.direction, angularDiameter: sun.moon.angularDiameter } : null,
    });
    if (B.hasEphemeris && Number.isFinite(tick)) {
      const p = this.flight.pose;
      const bias = p && p.body === 0 ? 1 - smooth(BIAS_FROM_M, BIAS_TO_M, p.h) : 0;
      B.feedEphemeris(tick, bias);
    }
  }

  /** The camera after the controls: where the observer is. The result is what to write to the camera when `changed`. */
  step(x: number, y: number, z: number, yaw: number, pitch: number, roll = 0, dt = 0, drive: Drive | null = null): FlightOut {
    return this.flight.step(x, y, z, yaw, pitch, roll, dt, drive);
  }

  /** The numbers of this frame's pose. */
  prepare(): void {
    const p = this.flight.pose;
    this.pose = p;
    this.active = !!p;
    if (!p) { this.h = 0; this.nf = 1; this.nfc = 1; this.share = 1; this.nearOff = false; this.atHome = true; this.inShadow = false; this.fb = []; return; }
    const home = p.body === 0;
    this.h = p.h;
    this.nf = home ? nearFade(p.h) : 0;
    this.nfc = home ? cloudFade(p.h) : 0;
    this.share = groundShare(len(p.rel) - p.h, len(p.rel));
    this.nearOff = !home || p.h > NEAR_HIDE_M;
    this.atHome = home;
    this.inShadow = this.bodies.inShadow(p);
    const fb = this.bodies.farBodies(p);
    fb.sort((a, b) => (a.dist - a.r) - (b.dist - b.r));
    this.fb = fb;
  }

  /**
   * The eye's light (eyeLum.ts), from the tables' light at the eye: `sky` the luminance of the diffuse light on a horizontal surface, `beam` the star's beam normal to itself, `sinEl` the sine of its
   * elevation. Those are the home planet's; at another body there is no sky pass and the sun message's numbers are the home planet's, so the sky is none, the beam is the star's own (flux, no air)
   * unless the body or another covers it, and its elevation is the registry's.
   */
  eyeLum(sky: number, beam: number, sinEl: number): EyeLum {
    const p = this.pose;
    if (!this.active || !p) return eyeLum(sky, beam, sinEl, 1);
    if (this.atHome) return eyeLum(sky, beam, sinEl, this.share);
    const s = this.bodies.sunSeen(p);
    return eyeLum(0, this.inShadow ? 0 : this.flux * STAR_BEAM_LUM, s ? s.dir[1] : 0, this.share);
  }

  /** Where the star's disc and glare go, in the render axes. `server`: the sun message's direction in the render axes (unit), `serverAngR` its angular radius. Near the ground it is the server's; away it is the registry's. */
  sunPlacement(server: V3, serverAngR: number): SunPlacement {
    const p = this.pose;
    if (!p) return { dir: server, hidden: 0, angR: serverAngR, away: false };
    const s = this.bodies.sunSeen(p);
    if (!s) return { dir: server, hidden: 0, angR: serverAngR, away: false };
    const w = p.body === 0 ? smooth(SUN_BLEND_LO, SUN_BLEND_HI, p.h) : 1;
    const dir = w <= 0 ? server : norm([server[0] + (s.dir[0] - server[0]) * w, server[1] + (s.dir[1] - server[1]) * w, server[2] + (s.dir[2] - server[2]) * w]);
    const angR = w <= 0 ? serverAngR : serverAngR + (s.angR - serverAngR) * w;
    return { dir, hidden: this.bodies.starHidden(p), angR, away: p.body !== 0 || p.h > SUN_BLEND_LO };
  }

  /**
   * The moon's glare (moon.ts). `beam`: the moon's beam at the eye, through the air and no cloud (the sun message's moon, as observerSun gives it from the tables at the eye's
   * height); null with no moon. Where it is and how big comes from the registry.
   */
  moonGlare(beam: ArrayLike<number> | null): MoonGlare | null {
    const p = this.pose;
    if (!p) return null;
    const m = this.bodies.moonSeen(p);
    if (!m) return null;
    const b: V3 = beam ? [Math.max(0, beam[0]!), Math.max(0, beam[1]!), Math.max(0, beam[2]!)] : [0, 0, 0];
    // hidden when the ray to the moon meets the planet (the ground's horizon, for an eye on or near it; the whole disc, for an eye in orbit)
    let hidden = false;
    const home = this.fb.find(b => b.kind === 'world');
    if (home) hidden = rayHits(home.c, home.r, m.dir);
    return { dir: m.dir, angR: m.angR, beam: b, hidden };
  }

  /** The stars' and the galaxy's view of the observer (universe position and the local frame), or null before the registry has a pose. */
  starView(): { worldPos: V3; frame: M3 } | null {
    return this.pose ? this.bodies.starView(this.pose) : null;
  }

  /** A world and the nearest rock that hide the stars (centre from the eye in the render axes, radius). For StarField.setSpace / GalaxyGlow.setSpace. */
  starSpace(): { home: [number, number, number, number] | null; occ: [number, number, number, number] | null } {
    const home = this.fb.find(b => b.kind === 'world');
    const rock = this.fb.find(b => b.kind === 'rock');
    const h4 = home ? ([home.c[0], home.c[1], home.c[2], home.r] as [number, number, number, number]) : null;
    const o4 = rock ? ([rock.c[0], rock.c[1], rock.c[2], rock.r * sizeScale(rock.ang)] as [number, number, number, number]) : null;
    return { home: h4, occ: o4 };
  }

  /** The far shell for this frame. `eGain`: the display exposure gain times the sky curve's constant; `pixAng`: the angle one pixel spans (rad). */
  paint(eGain: number, srgbOut: boolean, cloudProbe: THREE.Texture | null, pixAng: number, skyTrans: THREE.Texture | null, skyElH: number): void {
    handover.update(this.active && this.atHome && worldTiles.haveGlobal, this.h, this.nfc, worldTiles.patchHalf);
    this.volGone = handover.avail >= 0.999 && this.h > handover.reach + VOLUME_TOP_M;
    if (!this.active) { this.shell.mesh.visible = false; this.cloud.update(null, null, pixAng); return; }
    const list = this.fb.slice(0, FAR_MAX_BODIES).map<FarBody>(b => b.kind === 'rock' ? { ...b, r: b.r * sizeScale(b.ang) } : b);
    const f: FarShellFrame = {
      bodies: list,
      home: list.findIndex(b => b.kind === 'world'),
      air: [this.bodies.tau[0], this.bodies.tau[1], this.bodies.tau[2], AEROSOL_TAU],
      worldA: this.nf < 1 || handover.avail > 0 ? 1 : 0,
      skyTrans: this.atHome ? skyTrans : null, skyElH,
      eGain, srgbOut, cloudProbe,
    };
    this.shell.update(f, pixAng);
    this.cloud.update(f, this.atHome ? list.find(b => b.kind === 'world') ?? null : null, pixAng);
  }

  /** The observer's place on the home planet's tile chart (east and north of the world's point along the ground, m; height above the sea, m): what the server's `loc` message states. Valid
   *  anywhere on the planet, in the tile regime and out of it; null at another body or before there is a pose. */
  chartPos(): { east: number; north: number; h: number } | null {
    const p = this.pose;
    if (!p || p.body !== 0) return null;
    return this.bodies.tileOf(p.pos);
  }

  /** A line for the panel. */
  describe(): string {
    if (!this.pose) return 'no pose yet (the sun message has no frame or world position, or has not come)';
    const b = this.bodies.list[this.pose.body];
    const km = this.h / 1000;
    return `${this.flight.mode} at ${b ? b.id : '?'}  height ${km >= 100 ? km.toFixed(0) : km.toFixed(2)} km  near field ${this.nf.toFixed(2)}${this.nearOff ? ' (hidden)' : ''}  planet's share of the view ${this.share.toFixed(2)}${this.atHome ? '' : ' (not the home planet: no sky pass)'}${this.inShadow ? '  in shadow' : ''}`;
  }

  dispose(): void { this.shell.dispose(); this.cloud.dispose(); }
}

/** The size a rock is drawn at, relative to its true size: a speck is shown larger (the sky's choice, as before), a disc with room to show its face is true. */
function sizeScale(angR: number): number {
  return MOON_SIZE_SCALE + (1 - MOON_SIZE_SCALE) * smooth(SIZE_EASE_LO, SIZE_EASE_HI, angR);
}

/** Whether the ray from the eye along unit `dir` meets the sphere (centre `c` from the eye, radius `r`) in front of it. */
function rayHits(c: ArrayLike<number>, r: number, dir: ArrayLike<number>): boolean {
  const t0 = dot(c, dir);                       // along the ray to the point nearest the centre
  const s2 = r * r - (dot(c, c) - t0 * t0);
  return s2 > 0 && (t0 - Math.sqrt(s2)) > 0;
}

